import Immutable from 'immutable';
import moment from 'moment';
import { createSelector } from 'reselect';
import { coursesSelector } from '../../Courses/selectors';
import { cohortsSelector } from '../../Cohorts/selectors';
import { selectCoursePromoData } from '../../CoursePromoData/selectors';
import {
  ENROLMENT_CLASS_SELECTION_DEFAULT_COHORT,
  ENROLMENT_CLASS_SELECTION_ACCESS_CODE,
} from './constants';
import { priceFormat } from '../../../web/utilities/utilities';

const selectEnrolmentModal = (state) => state.EnrolmentModal || Immutable.Map();

export const selectEnrolmentInProgress = createSelector(
  selectEnrolmentModal,
  (enrolmentModal) => enrolmentModal.get('inProgress', false)
);

export const selectEnrolmentMode = createSelector(
  selectEnrolmentModal,
  (enrolmentModal) => enrolmentModal.get('mode')
);

export const selectExternalEnrolmentLink = createSelector(
  selectEnrolmentModal,
  (enrolmentModal) => enrolmentModal.get('externalEnrolmentLink')
);

export const selectCourseForEnrolment = createSelector(
  selectEnrolmentModal,
  coursesSelector,
  (enrolmentModal, courses) => courses.get(enrolmentModal.get('course', null))
);

export const selectEnrolmentError = createSelector(
  selectEnrolmentModal,
  (enrolmentModal) => enrolmentModal.get('error')
);

export const selectDefaultCohortForEnrolment = createSelector(
  selectCourseForEnrolment,
  cohortsSelector,
  selectCoursePromoData,
  (course, cohorts, coursePromoData) => {
    if (course) {
      const cohort = cohorts.get(course.get('defaultCohort'), Immutable.Map());

      // sometimes we have institution specific pricing for the default cohort in the course promo data
      const promoData = coursePromoData.getIn(
        ['byCoursePath', course.get('path')],
        Immutable.Map()
      );
      return promoData.size
        ? cohort.merge({
            studentPrice: promoData.get('studentPrice'),
            currency: promoData.get('currency'),
          })
        : cohort;
    }
    return null;
  }
);

const OPEN_CLASS_VISIBILITY_LEVELS = Immutable.fromJS(['open', 'paid']);

export const selectClassOptions = createSelector(
  selectDefaultCohortForEnrolment,
  (defaultCohort) => {
    const showDefaultCohort = OPEN_CLASS_VISIBILITY_LEVELS.includes(
      defaultCohort.get('visibilityLevel')
    );

    const options = [{ value: '', label: 'Select a class' }];
    if (showDefaultCohort) {
      options.push({
        value: 'defaultCohort',
        label: `${defaultCohort.get('title')} (Open Class)`,
      });
    }
    options.push({
      value: 'accessCode',
      label: 'Private Class (Access Code Required)',
    });

    return options;
  }
);

export const selectClassOption = createSelector(
  selectEnrolmentModal,
  (enrolmentModal) => enrolmentModal.get('classOption', '')
);

export const selectAccessCode = createSelector(
  selectEnrolmentModal,
  (enrolmentModal) => enrolmentModal.getIn(['accessCode', 'value'], '')
);

export const selectAccessCodeError = createSelector(
  selectEnrolmentModal,
  (enrolmentModal) => enrolmentModal.getIn(['accessCode', 'error'], '')
);

export const selectAccessCodeFormLoading = createSelector(
  selectEnrolmentModal,
  (enrolmentModal) => enrolmentModal.getIn(['accessCode', 'loading'], false)
);

export const selectAccessCodeCohortForEnrolment = createSelector(
  selectEnrolmentModal,
  cohortsSelector,
  (enrolmentModal, cohorts) =>
    cohorts.get(enrolmentModal.getIn(['accessCode', 'cohort'], null))
);

export const selectCohortForEnrolment = createSelector(
  selectDefaultCohortForEnrolment,
  selectAccessCodeCohortForEnrolment,
  selectClassOption,
  (defaultCohort, accessCodeCohort, classOption) => {
    switch (classOption) {
      case ENROLMENT_CLASS_SELECTION_DEFAULT_COHORT:
        return defaultCohort;
      case ENROLMENT_CLASS_SELECTION_ACCESS_CODE:
        return accessCodeCohort;
      default:
        return null;
    }
  }
);

export const selectCohortStartDate = createSelector(
  selectCohortForEnrolment,
  (selectedCohort) => {
    let startDate = 'Available Now';
    if (selectedCohort) {
      const momentDate = moment(selectedCohort.get('startDate'));
      if (momentDate.isValid() && momentDate.isAfter()) {
        startDate = momentDate.format('DD MMM YYYY');
      }
    }
    return startDate;
  }
);

export const selectCohortCost = createSelector(
  selectCohortForEnrolment,
  (selectedCohort) => {
    let cost = 'Free';
    if (
      selectedCohort &&
      selectedCohort.get('paidCohort') &&
      selectedCohort.get('studentPrice')
    ) {
      cost = priceFormat(
        selectedCohort.get('studentPrice'),
        selectedCohort.get('currency')
      );
    }
    return cost;
  }
);
